import { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import { Node } from 'reactflow';
import { EmailStepConfig } from './EmailStepConfig';
import { SMSStepConfig } from './SMSStepConfig';
import { AICallStepConfig } from './AICallStepConfig';
import { DelayStepConfig } from './DelayStepConfig';
import { ConditionStepConfig } from './ConditionStepConfig';
import { WebhookStepConfig } from './WebhookStepConfig';

interface StepConfigModalProps {
  node: Node;
  isOpen: boolean;
  onClose: () => void;
  onSave: (nodeId: string, config: any) => void;
}

export function StepConfigModal({ node, isOpen, onClose, onSave }: StepConfigModalProps) {
  const [config, setConfig] = useState<any>(node.data.config || {});
  const [stepName, setStepName] = useState(node.data.label || '');
  const [errors, setErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'tracking'>('basic');

  useEffect(() => {
    const nextConfig = node.data.config || {};
    // Direct Mail steps default to the LettrLabs inbound webhook URL (no secrets are stored here; it's a public URL)
    if (node.type === 'direct_mail') {
      setConfig({
        method: 'POST',
        content_type: 'application/json',
        body:
          nextConfig.body ||
          '{\n  "journey_id": "{{journey_id}}",\n  "step_id": "{{step_id}}",\n  "customer_id": "{{customer_id}}",\n  "first_name": "{{first_name}}",\n  "last_name": "{{last_name}}",\n  "email": "{{email}}",\n  "phone": "{{phone}}",\n  "qr_tracking_url": "{{qr_tracking_url}}",\n  "qr_image_url": "{{qr_image_url}}"\n}',
        url: nextConfig.url || import.meta.env.VITE_LETTRLABS_INBOUND_WEBHOOK || '',
        ...nextConfig,
      });
    } else {
      setConfig(nextConfig);
    }
    setStepName(node.data.label || '');
    setErrors([]);
  }, [node]);

  if (!isOpen) return null;

  const validateConfig = (): boolean => {
    const newErrors: string[] = [];

    if (!stepName.trim()) {
      newErrors.push('Step name is required');
    }

    // Type-specific validation
    switch (node.type) {
      case 'email':
        if (!config.template_id) newErrors.push('Email template is required');
        if (!config.subject) newErrors.push('Subject line is required');
        break;
      case 'sms':
        if (!config.template_id) newErrors.push('SMS template is required');
        if (!config.message) newErrors.push('Message is required');
        if (config.message && config.message.length > 160) {
          newErrors.push('SMS message exceeds 160 characters');
        }
        break;
      case 'ai_call':
        if (!config.template_id) newErrors.push('Call script is required');
        if (!config.voice_id) newErrors.push('Voice selection is required');
        break;
      case 'delay':
        if (!config.delay_minutes || config.delay_minutes <= 0) {
          newErrors.push('Delay duration must be greater than 0');
        }
        break;
      case 'condition':
        if (!config.conditions || config.conditions.length === 0) {
          newErrors.push('At least one condition is required');
        }
        break;
      case 'webhook':
        if (!config.url) newErrors.push('Webhook URL is required');
        if (config.url && !isValidUrl(config.url)) {
          newErrors.push('Invalid webhook URL');
        }
        break;
      case 'direct_mail':
        if (!config.url) newErrors.push('LettrLabs inbound webhook URL is required');
        if (config.url && !isValidUrl(config.url)) {
          newErrors.push('Invalid LettrLabs inbound webhook URL');
        }
        break;
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = () => {
    if (validateConfig()) {
      onSave(node.id, {
        label: stepName,
        config: config,
      });
      onClose();
    }
  };

  const renderConfigContent = () => {
    switch (node.type) {
      case 'email':
        return <EmailStepConfig config={config} onChange={setConfig} />;
      case 'sms':
        return <SMSStepConfig config={config} onChange={setConfig} />;
      case 'ai_call':
        return <AICallStepConfig config={config} onChange={setConfig} />;
      case 'delay':
        return <DelayStepConfig config={config} onChange={setConfig} />;
      case 'condition':
        return <ConditionStepConfig config={config} onChange={setConfig} />;
      case 'webhook':
        return <WebhookStepConfig config={config} onChange={setConfig} />;
      case 'direct_mail':
        // Direct Mail is configured using the same webhook config UI (POST JSON to inbound webhook)
        return <WebhookStepConfig config={config} onChange={setConfig} />;
      default:
        return <div className="text-gray-500">No configuration available for this step type</div>;
    }
  };

  const getStepTypeLabel = () => {
    const labels: { [key: string]: string } = {
      email: 'Email Step',
      sms: 'SMS Step',
      ai_call: 'AI Call Step',
      delay: 'Delay Step',
      condition: 'Condition Step',
      webhook: 'Webhook Step',
      direct_mail: 'Direct Mail Step (LettrLabs)',
    };
    return labels[node.type || 'unknown'] || 'Step Configuration';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{getStepTypeLabel()}</h2>
            <p className="text-sm text-gray-500 mt-1">Configure the details for this workflow step</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  Please fix the following errors:
                </h3>
                <ul className="text-sm text-red-700 space-y-1">
                  {errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <nav className="flex gap-4 -mb-px">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Basic Settings
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'advanced'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Advanced
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'tracking'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tracking & Analytics
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Step Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={stepName}
                  onChange={(e) => setStepName(e.target.value)}
                  placeholder="e.g., Welcome Email"
                  className="input w-full"
                />
                <p className="mt-1 text-xs text-gray-500">
                  A descriptive name for this step (visible in the workflow canvas)
                </p>
              </div>

              {/* Type-specific Configuration */}
              {renderConfigContent()}
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
                
                {/* Error Handling */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Error Handling
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.continue_on_error || false}
                        onChange={(e) =>
                          setConfig({ ...config, continue_on_error: e.target.checked })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Continue workflow if this step fails
                      </span>
                    </label>

                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Retry Attempts
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={config.retry_attempts || 0}
                        onChange={(e) =>
                          setConfig({ ...config, retry_attempts: parseInt(e.target.value) })
                        }
                        className="input w-32"
                      />
                    </div>
                  </div>
                </div>

                {/* Execution Schedule */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Execution Schedule
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.business_hours_only || false}
                        onChange={(e) =>
                          setConfig({ ...config, business_hours_only: e.target.checked })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Execute only during business hours (9 AM - 5 PM)
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.skip_weekends || false}
                        onChange={(e) =>
                          setConfig({ ...config, skip_weekends: e.target.checked })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Skip weekends</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tracking' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Tracking & Analytics
                </h3>

                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.track_opens !== false}
                      onChange={(e) => setConfig({ ...config, track_opens: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Track opens and views</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.track_clicks !== false}
                      onChange={(e) => setConfig({ ...config, track_clicks: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Track link clicks</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.track_conversions !== false}
                      onChange={(e) =>
                        setConfig({ ...config, track_conversions: e.target.checked })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Track conversions</span>
                  </label>

                  <div className="pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Event Tags
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., winback, onboarding, renewal"
                      value={config.event_tags || ''}
                      onChange={(e) => setConfig({ ...config, event_tags: e.target.value })}
                      className="input w-full"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Comma-separated tags for analytics filtering
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
